/**
 * Role-Based Menu Access Configuration
 *
 * Simple & Scalable:
 * - Add a role with accessAll: true for full menu access
 * - Add a role with menuItems array for restricted access
 * - Each menu item ID in the array grants that item's access
 *
 * Example:
 * - SuperAdmin: Can see all menus
 * - User: Can only see Dashboard and Ads
 * - AdManager: Can see Dashboard, Ads, and Device Status
 */

export const roleMenuConfig = {
  // Admin/SuperAdmin - Full access to all menus
  SuperAdmin: {
    accessAll: true,
    defaultRedirect: "/dashboard",
  },

  Admin: {
    accessAll: true,
    defaultRedirect: "/dashboard",
  },

  // User role - Limited to specific menus
  User: {
    accessAll: false,
    menuItems: ["Dashboard", "ads", "device-heartbeat-status"],
    defaultRedirect: "/ads/daily-stats",
  },

  // AdManager - Can manage ads and see device status
  AdManager: {
    accessAll: false,
    menuItems: [
      // "Dashboard",
      "ads-daily-stats",
      "ads-post",
      "ads-logs",
      "device-heartbeat",
      // "logs",
      "advertisers",
      "campaigns",
      "assign-media",
      "device-list",
    ],
    defaultRedirect: "/ads/daily-stats",
  },

  // DeviceManager - Can only manage devices
  DeviceManager: {
    accessAll: false,
    menuItems: ["Dashboard", "device-heartbeat-status", "logs"],
    defaultRedirect: "/device-heartbeat-status",
  },

  // Manager - Can manage users and view reports
  Manager: {
    accessAll: false,
    menuItems: [
      "Dashboard",
      "user-management",
      "logs",
      "device-heartbeat-status",
    ],
    defaultRedirect: "/dashboard",
  },

  // Viewer - Read-only access
  Viewer: {
    accessAll: false,
    menuItems: ["Dashboard", "logs", "device-heartbeat-status"],
    defaultRedirect: "/dashboard",
  },
};

/**
 * Get menu items allowed for a role
 * @param {string} role - User role
 * @returns {array|null} Array of menu item IDs or null if full access
 */
export const getAllowedMenuItems = (role) => {
  const config = roleMenuConfig[role];
  if (!config) return [];
  return config.accessAll ? null : config.menuItems;
};

/**
 * Get default redirect for a role
 * @param {string} role - User role
 * @returns {string|null} Default redirect path
 */
export const getDefaultRedirect = (role) => {
  const config = roleMenuConfig[role];
  return config?.defaultRedirect || null;
};

/**
 * Check if role has full access
 * @param {string} role - User role
 * @returns {boolean} True if role has full menu access
 */
export const hasAccessToAll = (role) => {
  const config = roleMenuConfig[role];
  return config?.accessAll || false;
};
